import { memo, useEffect, useRef } from 'react'
import { cn } from '../../lib/utils'

// Both exports carry alpha: the WebM is VP9 + alpha, the MP4 is hvc1 with
// ContainsAlphaChannel. WebKit decodes only the MP4 form, so it is sent there;
// everything else gets the WebM (broadly supported, ~a third of the size).
const WEBM = '/video/yami-kuro-pushup.webm'
const MP4 = '/video/yami-kuro-pushup.mp4'
const POSTER = '/video/yami-kuro-pushup-poster.png'

// He ends the clip standing — a good resting pose — so it holds there and
// restarts after a randomised gap. A fixed interval reads as scheduled; a wide
// 5-20s spread reads like he just felt like another set.
const GAP_MIN = 5000
const GAP_MAX = 20000

/**
 * The Yami Kuro mascot. Isolated and memoized so its video loop never triggers
 * a re-render in the hero around it. Gated behind prefers-reduced-motion, where
 * it holds on frame 0 rather than autoplaying.
 */
export const YamiKuro = memo(function YamiKuro({
  width = 150,
  className,
}: {
  width?: number
  className?: string
}) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const v = ref.current
    if (!v) return

    // Pick the source in JS: canPlayType is unreliable for hvc1-alpha (Chromium
    // on macOS returns "" then decodes it fine). ?v=mp4 / ?v=webm force a path.
    const ua = navigator.userAgent
    const webkitOnly = /Safari/.test(ua) && !/Chrome|Chromium|Android|Edg\/|OPR\//.test(ua)
    const forced = new URLSearchParams(window.location.search).get('v')
    const useMp4 = forced ? forced === 'mp4' : webkitOnly
    v.src = useMp4 ? MP4 : WEBM

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      // An autoplaying loop is exactly what reduced-motion asks us not to ship.
      v.load() // paints the poster / first frame, then holds.
      return
    }

    let timer: number | undefined
    const onEnded = () => {
      window.clearTimeout(timer)
      const gap = GAP_MIN + Math.random() * (GAP_MAX - GAP_MIN)
      timer = window.setTimeout(() => {
        v.currentTime = 0
        v.play()?.catch(() => {})
      }, gap)
    }
    v.addEventListener('ended', onEnded)
    v.play()?.catch(() => {})

    return () => {
      v.removeEventListener('ended', onEnded)
      window.clearTimeout(timer)
    }
  }, [])

  return (
    <figure
      className={cn('relative block pointer-events-none select-none', className)}
      style={{ width, margin: 0, flexShrink: 0 }}
      aria-hidden="true"
    >
      <span
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
            'radial-gradient(62% 42% at 50% 60%, rgba(232,197,160,0.14), transparent 72%)',
        }}
      />
      <video
        ref={ref}
        poster={POSTER}
        width={720}
        height={720}
        muted
        playsInline
        className="block w-full h-auto"
      />
    </figure>
  )
})
